import {
  ChevronLeft,
  ChevronRight,
  X,
  Maximize2,
  Minimize2,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";

export interface ZenCarouselHeaderProps {
  currentIndex: number;
  total: number;
  stepTitle?: string | null;
  filterScope?: string;
  onPrev: () => void;
  onNext: () => void;
  showSidebar: boolean;
  onToggleSidebar: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onClose: () => void;
}

export function ZenCarouselHeader({
  currentIndex,
  total,
  stepTitle,
  filterScope,
  onPrev,
  onNext,
  showSidebar,
  onToggleSidebar,
  isFullscreen,
  onToggleFullscreen,
  onClose,
}: ZenCarouselHeaderProps) {
  const scopeLabel =
    filterScope === "problem"
      ? "Problems only"
      : filterScope === "strength"
        ? "Strengths only"
        : filterScope === "all"
          ? null
          : filterScope;

  return (
    <header className="flex h-13 shrink-0 items-center justify-between border-b border-border/80 bg-muted/40 px-4 sm:px-6">
      <div className="flex items-center gap-3">
        <span className="rounded-md border border-border/80 bg-background px-2.5 py-1 font-mono text-xs font-semibold text-foreground shadow-2xs">
          {currentIndex + 1} of {total}
        </span>
        {scopeLabel && (
          <span className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-border/80 bg-background px-2.5 py-1 text-2xs font-medium text-muted-foreground shadow-2xs">
            <span className="size-1.5 rounded-full bg-primary/70" />
            <span>Scope:</span>
            <span className="font-semibold text-foreground">{scopeLabel}</span>
          </span>
        )}
        <div className="flex items-center gap-1">
          <SimpleTooltip
            content={
              <div className="flex items-center gap-1.5 text-xs">
                <span>Previous finding</span>
                <div className="flex items-center gap-0.5">
                  <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                    ←
                  </kbd>
                  <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                    k
                  </kbd>
                </div>
              </div>
            }
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={onPrev}
              disabled={currentIndex === 0}
              className="h-8 gap-1 px-2 text-xs font-medium"
              aria-label="Previous finding"
            >
              <ChevronLeft className="size-4" />
              <span className="hidden sm:inline">Previous</span>
            </Button>
          </SimpleTooltip>

          <SimpleTooltip
            content={
              <div className="flex items-center gap-1.5 text-xs">
                <span>Next finding</span>
                <div className="flex items-center gap-0.5">
                  <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                    →
                  </kbd>
                  <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                    j
                  </kbd>
                </div>
              </div>
            }
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={onNext}
              disabled={currentIndex === total - 1}
              className="h-8 gap-1 px-2 text-xs font-medium"
              aria-label="Next finding"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="size-4" />
            </Button>
          </SimpleTooltip>
        </div>
        {stepTitle && (
          <span className="hidden xl:inline text-xs font-medium text-muted-foreground truncate max-w-xs">
            {stepTitle}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Sidebar toggle button */}
        <SimpleTooltip
          content={
            <div className="flex items-center gap-1.5 text-xs">
              <span>{showSidebar ? "Hide sidebar" : "Show sidebar"}</span>
              <div className="flex items-center gap-0.5">
                <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                  [
                </kbd>
                <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                  b
                </kbd>
              </div>
            </div>
          }
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-8 rounded-md"
            onClick={onToggleSidebar}
            aria-label={showSidebar ? "Hide sidebar" : "Show sidebar"}
          >
            {showSidebar ? (
              <PanelRightClose className="size-4" />
            ) : (
              <PanelRightOpen className="size-4" />
            )}
          </Button>
        </SimpleTooltip>

        {/* Native Fullscreen toggle button */}
        <SimpleTooltip
          content={
            <div className="flex items-center gap-1.5 text-xs">
              <span>
                {isFullscreen
                  ? "Exit display fullscreen"
                  : "Enter display fullscreen"}
              </span>
              <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                f
              </kbd>
            </div>
          }
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-8 rounded-md"
            onClick={onToggleFullscreen}
            aria-label={
              isFullscreen
                ? "Exit display fullscreen"
                : "Enter display fullscreen"
            }
          >
            {isFullscreen ? (
              <Minimize2 className="size-4" />
            ) : (
              <Maximize2 className="size-4" />
            )}
          </Button>
        </SimpleTooltip>

        {/* Close button */}
        <SimpleTooltip
          content={
            <div className="flex items-center gap-1.5 text-xs">
              <span>Close zen mode</span>
              <kbd className="rounded border border-background/25 bg-background/20 px-1 font-mono text-2xs font-semibold text-background">
                Esc
              </kbd>
            </div>
          }
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-8 rounded-md hover:bg-destructive/10 hover:text-destructive"
            onClick={onClose}
            aria-label="Close zen mode"
          >
            <X className="size-4" />
          </Button>
        </SimpleTooltip>
      </div>
    </header>
  );
}
